import React, { useEffect, useMemo, useState } from 'react';
import {
	ChangeSet,
	EntityId,
	EntityType,
	ProjectState,
	resolveEntityName,
	VariableInstanceState,
	VariableDeclarationState,
	getParentCanvasEntity,
	READABLE_ENTITY_TYPES,
	lowercaseFirstLetter,
	StateMutationErrorCode,
	toSentenceCase,
	safeResolveEntityName,
	StateMutationAction,
} from '@elyx-code/project-logic-tree';
import { Alert, Collapse } from '@mui/material';
import { EditorService } from '../../../services/editor';
import DataTypeBuilder from '../../data-type/DataTypeBuilder';
import Tooltip from '../../../lib/tooltip/Tooltip';
import Button from '../../../lib/button/Button';
import { useParams } from 'react-router-dom';
import EntityIcon from '../../entity-icon/EntityIcon';
import EntityDialogSectionHeader from '../../EntityDialogSectionHeader';
import { getCanvasEntityDraggableContainerDOMId } from '../../../services/editor/ui';
import DynamicValueEntry from '../../dynamic-value/DynamicValueEntry';

export interface IVariableInstanceProps {
	project: EditorService;
	entity?: VariableInstanceState;
	onClose?: (e: any) => void;
}

export enum VariableInstanceError {
	DeclarationRequired = 'declaration-required',
}

export function fromVariableInstanceErrorToReadableMessage(
	error: VariableInstanceError,
): string {
	switch (error) {
		case VariableInstanceError.DeclarationRequired:
			return 'Declaration is required';
		default:
			return toSentenceCase(error);
	}
}

function calculateVariableInstanceCreationFormErrors(
	entity: VariableInstanceState,
): VariableInstanceError[] {
	const errors: VariableInstanceError[] = [];

	if (!entity.declaration) {
		errors.push(VariableInstanceError.DeclarationRequired);
	}

	return errors;
}

const VariableInstance: React.FC<IVariableInstanceProps> = ({
	project,
	entity: _entity,
	onClose,
}) => {
	const [updateKey, setUpdateKey] = useState(0);
	const { entityId } = useParams<{
		entityType: EntityType;
		entityId: EntityId;
	}>();

	const entity: VariableInstanceState | null =
		_entity ||
		(project?.logic?.get(entityId) as VariableInstanceState | null) ||
		null;

	const [declarationValue, setDeclarationValue] =
		useState<VariableDeclarationState | null>(entity.declaration || null);

	const [defaultValueSectionOpen, setDefaultValueSectionOpen] = useState(false);
	const [baseFunctionalitySectionOpen, setBaseFunctionalitySectionOpen] =
		useState(false);

	const [errors, setErrors] = useState<VariableInstanceError[]>(
		calculateVariableInstanceCreationFormErrors(entity),
	);

	useEffect(() => {
		const newErrors = calculateVariableInstanceCreationFormErrors(entity);
		setErrors(newErrors);
		project.emit(entity.id, {});
	}, [updateKey, declarationValue]);

	const declarationsOptions = useMemo(() => {
		const options = [entity.declaration];

		return options;
	}, [entity.parent]);

	const dataType = entity.getDataType(null);

	return (
		<div
			style={{
				display: 'flex',
				flexGrow: 1,
				height: '100%',
				overflowY: 'scroll',
			}}
		>
			{/* We need the 'span' for the react ref */}
			<span
				style={{
					maxHeight: '100%',
					maxWidth: '100%',
					flexGrow: 1,
					height: '100%',
				}}
			>
				<div
					style={{
						display: 'flex',
						flexDirection: 'column',
						height: '100%',
						gap: '4px',
						justifyContent: 'space-between',
					}}
				>
					<span
						style={{
							maxWidth: '100%',
							maxHeight: '100%',
							overflowY: 'scroll',
							display: 'flex',
							flexDirection: 'column',
							height: '100%',
						}}
					>
						<section
							style={{
								display: 'flex',
								flexDirection: 'column',
								paddingBottom: 0,
							}}
						>
							<EntityDialogSectionHeader
								title={`Variable name: ${resolveEntityName(
									entity,
									project.logic,
								)}`}
								subtitle={
									entity.declaration.description ||
									'The variable declaration has no description'
								}
								hideShowSuffix='the name and description section'
								expandable={false}
								headerEndIcon={
									// Button to edit declaration
									<Button
										variant='outlined'
										color='primary'
										iconEnd='fa-solid fa-pencil'
										size='small'
										onClick={() => {
											EditorService.navigate(
												`${project?.editorBasePath}/entity/${EntityType.VariableDeclaration}/${entity.declaration.id}`,
											);

											const canvasEntity = getParentCanvasEntity(
												entity.declaration,
											);

											const objectId =
												getCanvasEntityDraggableContainerDOMId(canvasEntity);

											project.canvas?.scrollToObjectById(objectId);
										}}
									>
										Declaration
									</Button>
								}
							/>
						</section>

						<hr
							style={{
								margin: '1px 0',
								border: '1px solid rgba(0,0,0,0.08)',
							}}
						/>

						<section
							style={{
								display: 'flex',
								flexDirection: 'column',
								paddingBottom: baseFunctionalitySectionOpen ? 20 : 0,
							}}
						>
							<EntityDialogSectionHeader
								title={`Represents the "${safeResolveEntityName(
									entity.declaration,
									project.logic,
								)}" ${READABLE_ENTITY_TYPES[
									EntityType.VariableDeclaration
								].singular.toLowerCase()}`}
								subtitle={`This variable instance represents an underlying declaration with an existing data type an metadata`}
								hideShowSuffix='the declaration section'
								open={baseFunctionalitySectionOpen}
								onToggle={() =>
									setBaseFunctionalitySectionOpen(!baseFunctionalitySectionOpen)
								}
								headerStartIcon={
									<EntityIcon
										entity={(entity as VariableInstanceState).declaration}
										project={project}
									/>
								}
							/>

							<Collapse in={baseFunctionalitySectionOpen}>
								<div
									style={{
										display: 'flex',
										flexDirection: 'column',
										gap: 20,
										marginLeft: 80,
										paddingRight: 40,
									}}
								>
									<DataTypeBuilder
										project={project}
										topParent={declarationValue}
										dataType={dataType}
										readOnly={!entity.editable}
										baseDataTypes={
											!!(
												entity as VariableInstanceState
											).declaration?.getDataType(null)
												? [
														(
															entity as VariableInstanceState
														).declaration?.getDataType(null),
													]
												: []
										}
										onChangeSet={() => {
											const newChangeSet = project.logic.addChangeSet(
												new ChangeSet(
													project.logic,
													ProjectState.sessionAuthor,
													new Date().toISOString(),
													entity,
													false,
													StateMutationAction.ChangeVarInstDeclaration,
												),
											);

											return newChangeSet;
										}}
									/>
								</div>
							</Collapse>
						</section>

						<hr
							style={{
								margin: '1px 0',
								border: '1px solid rgba(0,0,0,0.08)',
							}}
						/>

						<section
							style={{
								display: 'flex',
								flexDirection: 'column',
								paddingBottom: defaultValueSectionOpen ? 20 : 0,
							}}
						>
							<EntityDialogSectionHeader
								title='Default value'
								subtitle='Set the default value for this output'
								hideShowSuffix='the default value section'
								open={defaultValueSectionOpen}
								onToggle={() =>
									setDefaultValueSectionOpen(!defaultValueSectionOpen)
								}
							/>

							<Collapse in={defaultValueSectionOpen}>
								<div
									style={{
										marginTop: 6,
										display: 'flex',
										flexDirection: 'column',
										gap: 20,
										marginLeft: 80,
										paddingRight: 40,
									}}
								>
									<DynamicValueEntry
										entity={entity}
										project={project}
										parentDisabledReasons={
											!entity.editable
												? [StateMutationErrorCode.ActionOwnerEntityNotEditable]
												: []
										}
										onlyDefault
									>
										{(extraContent) => extraContent}
									</DynamicValueEntry>
								</div>
							</Collapse>
						</section>
					</span>

					{errors.length > 0 && (
						<div
							style={{
								marginTop: 20,
								marginRight: 40,
								marginLeft: 80,
							}}
						>
							<Alert severity='error'>
								{errors.length === 1 ? (
									<span
										style={{
											display: 'flex',
											alignItems: 'center',
											gap: 6,
										}}
									>
										<h4
											style={{
												margin: 0,
												display: 'flex',
											}}
										>
											Error:
										</h4>
										<p
											style={{
												margin: 0,
												display: 'flex',
											}}
										>
											{fromVariableInstanceErrorToReadableMessage(errors[0])}
										</p>
									</span>
								) : (
									<>
										<h4
											style={{
												margin: 0,
											}}
										>
											Errors
										</h4>
										<br className='quarter-space' />
										<ul
											style={{
												listStyleType: 'none',
												padding: 0,
											}}
										>
											{errors.map((error, index) => (
												<li
													key={index}
													style={{
														display: 'flex',
														alignItems: 'center',
													}}
												>
													{/* Circle */}
													<span
														style={{
															fontSize: 10,
															marginRight: 5,
														}}
													>
														&#9679;
													</span>
													<p
														style={{
															margin: 0,
															// fontSize: 10,
														}}
													>
														{fromVariableInstanceErrorToReadableMessage(error)}
													</p>
												</li>
											))}
										</ul>
									</>
								)}
							</Alert>
						</div>
					)}

					<div
						style={{
							alignSelf: 'flex-start',
							display: 'flex',
							flexDirection: 'row',
							justifyContent: 'flex-end',
							width: '100%',
							gap: '16px',
							padding: '0 40px',
						}}
					>
						<Button
							id='variable-instance-builder--done-button'
							onClick={(e: any) => {
								onClose?.(e);
							}}
							color='primary'
							disabled={errors.length > 0}
						>
							Done
						</Button>
						<Tooltip
							anchorQuerySelector='#variable-instance-builder--done-button'
							placement='bottom'
							openDelay={100}
						>
							{errors.length > 0
								? `Resolve the errors to save the ${lowercaseFirstLetter(
										READABLE_ENTITY_TYPES[entity.type].singular,
									)}`
								: `Save the ${lowercaseFirstLetter(
										READABLE_ENTITY_TYPES[entity.type].singular,
									)}`}
						</Tooltip>
					</div>
				</div>
			</span>
		</div>
	);
};

export default VariableInstance;
